<template>
  <Layout>
    <div class="integration-monitor">
      <div class="monitor-hero">
        <div>
          <div class="monitor-eyebrow">Monitoramento</div>
          <div class="monitor-title">Logs e integracoes</div>
          <div class="monitor-subtitle">
            Acompanhe a saude dos processos de integracao e navegue entre os ambientes conectados ao WebReceita.
          </div>
        </div>
        <div class="monitor-actions">
          <button class="btn btn-webreceita" @click="loadSelectedIntegration">
            Atualizar painel
          </button>
        </div>
      </div>

      <div class="monitor-toolbar">
        <div class="monitor-toolbar-copy">
          <div class="monitor-toolbar-label">Integracao selecionada</div>
          <div class="monitor-toolbar-value">{{ selectedIntegrationLabel }}</div>
        </div>
        <div class="monitor-select-wrap">
          <v-select
            class="style-select"
            label="label"
            :options="integrationOptions"
            :reduce="(item) => item.value"
            v-model="selectedIntegration"
          ></v-select>
        </div>
      </div>

      <div class="monitor-stats">
        <div class="monitor-stat-card neutral">
          <div class="monitor-stat-label">Total de eventos</div>
          <div class="monitor-stat-value">{{ selectedIntegration === 'sisdev' ? logs.length : 0 }}</div>
        </div>
        <div class="monitor-stat-card success">
          <div class="monitor-stat-label">Sucesso</div>
          <div class="monitor-stat-value">{{ selectedIntegration === 'sisdev' ? successCount : 0 }}</div>
        </div>
        <div class="monitor-stat-card danger">
          <div class="monitor-stat-label">Erros</div>
          <div class="monitor-stat-value">{{ selectedIntegration === 'sisdev' ? errorCount : 0 }}</div>
        </div>
        <div class="monitor-stat-card warning">
          <div class="monitor-stat-label">Outros eventos</div>
          <div class="monitor-stat-value">{{ selectedIntegration === 'sisdev' ? otherCount : 0 }}</div>
        </div>
      </div>

      <div v-if="selectedIntegration !== 'sisdev'" class="monitor-placeholder">
        <div class="monitor-placeholder-badge">Em breve</div>
        <div class="monitor-placeholder-title">{{ selectedIntegrationLabel }}</div>
        <div class="monitor-placeholder-copy">
          A estrutura do painel ja esta pronta para essa integracao. Quando o endpoint estiver disponivel, os dados
          entram aqui sem precisar refazer a interface.
        </div>
      </div>

      <div v-else class="monitor-table-card">
        <div class="monitor-table-head">
          <div>
            <div class="monitor-table-title">Historico de integracoes</div>
            <div class="monitor-table-subtitle">
              Base inicial reaproveitando os logs do SISDEV dentro do novo painel centralizado.
            </div>
          </div>
        </div>

        <div v-if="!isLoading && !historyLogs.length" class="monitor-empty">
          Nenhum log encontrado para a integracao selecionada.
        </div>

        <b-table
          v-else
          striped
          hover
          responsive="sm"
          borderless
          bordered
          outlined
          small
          :items="historyLogs"
          :fields="tableFields"
        >
          <template v-slot:cell(statusLabel)="row">
            <span :class="['monitor-status-pill', row.item.statusClass]">
              {{ row.item.statusLabel }}
            </span>
          </template>

          <template v-slot:cell(numeroReceitaComplementar)="row">
            <span class="monitor-chip">{{ row.item.numeroReceitaComplementar || '-' }}</span>
          </template>

          <template v-slot:cell(message)="row">
            <div class="monitor-message-cell">{{ row.item.message }}</div>
          </template>

          <template v-slot:cell(actions)="row">
            <button class="btn btn-outline-primary btn-sm" @click="openDetails(row.item)">
              Ver detalhes
            </button>
          </template>
        </b-table>
      </div>

      <b-modal
        v-model="showDetailsModal"
        title="Detalhes da integracao"
        size="lg"
        hide-footer
      >
        <div v-if="selectedLog">
          <div class="detail-grid">
            <div class="detail-item">
              <span class="detail-label">Data</span>
              <span class="detail-value">{{ formatDateTime(selectedLog.createdAt) }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Status</span>
              <span class="detail-value">{{ selectedLog.statusLabel }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Receita</span>
              <span class="detail-value">{{ selectedLog.receitaId || '-' }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Origem</span>
              <span class="detail-value">{{ selectedLog.origem || '-' }}</span>
            </div>
          </div>

          <div class="detail-block mt-3">
            <div class="detail-block-title">Mensagem</div>
            <div class="detail-box">{{ selectedLog.message || '-' }}</div>
          </div>

          <div class="detail-block mt-3" v-if="selectedLog.endpoint">
            <div class="detail-block-title">Endpoint</div>
            <div class="detail-box">{{ selectedLog.endpoint }}</div>
          </div>

          <div class="detail-block mt-3" v-if="selectedLog.rawPayload">
            <div class="detail-block-title">Payload bruto</div>
            <pre class="detail-code">{{ selectedLog.rawPayload }}</pre>
          </div>

          <div class="detail-block mt-3" v-if="selectedLog.rawResponse">
            <div class="detail-block-title">Resposta bruta</div>
            <pre class="detail-code">{{ selectedLog.rawResponse }}</pre>
          </div>
        </div>
      </b-modal>

      <b-overlay :show="isLoading" no-wrap />
    </div>
  </Layout>
</template>

<script>
import Layout from '@layouts/main'
import moment from 'moment'
import integrationLogService from '@/src/services/log-integration'

moment.locale('pt-br')

export default {
  components: {
    Layout,
  },
  data() {
    return {
      isLoading: false,
      selectedIntegration: 'sisdev',
      showDetailsModal: false,
      selectedLog: null,
      logs: [],
      integrationOptions: [
        { value: 'sisdev', label: 'SISDEV' },
        { value: 'agrodefesa', label: 'Agrodefesa' },
        { value: 'sigen', label: 'SIGEN+' },
        { value: 'siagro', label: 'SIAGRO' },
      ],
      tableFields: [
        { key: 'statusLabel', label: 'Status' },
        { key: 'numeroReceitaComplementar', label: 'Receita Complementar' },
        { key: 'message', label: 'Mensagem' },
        { key: 'actions', label: '' },
      ],
    }
  },
  computed: {
    selectedIntegrationLabel() {
      const option = this.integrationOptions.find((item) => item.value === this.selectedIntegration)
      return option ? option.label : 'Integracao'
    },
    historyLogs() {
      return this.logs
        .filter((log) => log.htmlPath !== 'sisdev_lista_receitas_response.html')
        .sort((current, next) => new Date(next.createdAt || 0) - new Date(current.createdAt || 0))
    },
    successCount() {
      return this.logs.filter((log) => log.statusGroup === 'SUCCESS').length
    },
    errorCount() {
      return this.logs.filter((log) => log.statusGroup === 'ERROR').length
    },
    otherCount() {
      return this.logs.filter((log) => log.statusGroup === 'OTHER').length
    },
  },
  watch: {
    selectedIntegration() {
      this.loadSelectedIntegration()
    },
  },
  mounted() {
    this.loadSelectedIntegration()
  },
  methods: {
    async loadSelectedIntegration() {
      if (this.selectedIntegration !== 'sisdev') {
        this.logs = []
        return
      }

      await this.loadLogs()
    },
    async loadLogs() {
      const token = this.$store.state.auth.currentUser.token
      this.isLoading = true

      try {
        const response = await integrationLogService.listar(token, 'sisdev')
        this.logs = this.normalizeLogs(response.data)
      } catch (error) {
        this.logs = []
        this.$bvToast.toast('Não foi possível carregar os logs do SISDEV.', {
          variant: 'danger',
          solid: false,
          noCloseButton: true,
          toaster: 'b-toaster-top-center',
        })
      } finally {
        this.isLoading = false
      }
    },
    normalizeLogs(payload) {
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload && payload.data)
          ? payload.data
          : Array.isArray(payload && payload.logs)
            ? payload.logs
            : []

      return list.map((item, index) => {
        const parsedPayload = this.parseStructuredValue(item.payload || item.request || item.body)
        const parsedResponse = this.parseStructuredValue(
          item.response || item.result || item.errorDetail || item.details
        )
        const statusLabel = this.extractStatusLabel(item)
        const createdAt =
          item.createdAt ||
          item.created_at ||
          item.dataCadastro ||
          item.data_cadastro ||
          item.timestamp ||
          item.date ||
          null

        const receitaId =
          item.idReceita ||
          item.receitaId ||
          item.id_receita ||
          item.receita_id ||
          item.payload && item.payload.idReceita ||
          item.request && item.request.idReceita ||
          null

        const endpoint =
          item.endpoint ||
          item.url ||
          item.route ||
          item.path ||
          null

        const receitasList = this.extractReceitasList(parsedPayload, parsedResponse)
        const message = this.extractMessage(item, parsedPayload, parsedResponse)
        const statusGroup = this.resolveStatusGroup(statusLabel, item, message, receitasList)

        return {
          id: item.id || `${receitaId || 'log'}-${index}`,
          createdAt,
          receitaId,
          numeroReceitaComplementar: this.extractNumeroReceitaComplementar(item, parsedPayload, parsedResponse),
          endpoint,
          origem: item.origem || item.source || 'sisdev',
          message: String(message),
          statusLabel,
          statusGroup,
          statusClass: this.resolveStatusClass(statusGroup),
          receitasList,
          receitasCount: receitasList.length,
          htmlPath: this.extractHtmlPath(item, parsedPayload, parsedResponse),
          rawPayload: this.stringifyBlock(parsedPayload),
          rawResponse: this.stringifyBlock(parsedResponse),
          raw: item,
        }
      })
    },
    extractStatusLabel(item) {
      return String(
        item.status ||
        item.level ||
        item.tipo ||
        item.evento ||
        item.event ||
        item.resultado ||
        'EVENTO'
      ).toUpperCase()
    },
    extractMessage(item, payload, response) {
      const directMessages = [
        item && item.messages,
        payload && payload.messages,
        payload && payload.data && payload.data.messages,
        response && response.messages,
        response && response.data && response.data.messages,
      ]

      for (let i = 0; i < directMessages.length; i += 1) {
        if (Array.isArray(directMessages[i]) && directMessages[i].length > 0) {
          return directMessages[i].join(' | ')
        }
      }

      return (
        item.message ||
        item.mensagem ||
        item.description ||
        item.descricao ||
        item.error ||
        item.erro ||
        item.responseMessage ||
        'Sem descrição informada.'
      )
    },
    extractNumeroReceitaComplementar(item, payload, response) {
      return (
        item.numeroReceitaComplementar ||
        item.receitaData && item.receitaData.numeroReceitaComplementar ||
        payload && payload.numeroReceitaComplementar ||
        payload && payload.receitaData && payload.receitaData.numeroReceitaComplementar ||
        payload && payload.data && payload.data.numeroReceitaComplementar ||
        payload && payload.data && payload.data.receitaData && payload.data.receitaData.numeroReceitaComplementar ||
        response && response.numeroReceitaComplementar ||
        response && response.receitaData && response.receitaData.numeroReceitaComplementar ||
        response && response.data && response.data.numeroReceitaComplementar ||
        response && response.data && response.data.receitaData && response.data.receitaData.numeroReceitaComplementar ||
        '-'
      )
    },
    extractHtmlPath(item, payload, response) {
      return (
        item.htmlPath ||
        item.responseHtmlPath ||
        item.receitaData && item.receitaData.responseHtmlPath ||
        payload && payload.htmlPath ||
        payload && payload.responseHtmlPath ||
        payload && payload.data && payload.data.htmlPath ||
        payload && payload.data && payload.data.responseHtmlPath ||
        response && response.htmlPath ||
        response && response.responseHtmlPath ||
        response && response.data && response.data.htmlPath ||
        response && response.data && response.data.responseHtmlPath ||
        ''
      )
    },
    resolveStatusGroup(statusLabel, item, messageText, receitasList) {
      const label = String(statusLabel || '').toLowerCase()
      const message = String(messageText || '').toLowerCase()
      const explicitErrorFields = [
        item && item.error,
        item && item.erro,
        item && item.errorMessage,
        item && item.exception,
        item && item.stack,
      ]
        .filter((value) => value != null && String(value).trim() !== '')
        .join(' ')
        .toLowerCase()

      if (Array.isArray(receitasList) && receitasList.length > 0) {
        return 'SUCCESS'
      }

      if (
        label.includes('erro') ||
        label.includes('error') ||
        label.includes('falha') ||
        label.includes('failed') ||
        explicitErrorFields.includes('erro') ||
        explicitErrorFields.includes('error') ||
        explicitErrorFields.includes('exception') ||
        explicitErrorFields.includes('falha') ||
        message.includes('erro') ||
        message.includes('exception') ||
        message.includes('falha')
      ) {
        return 'ERROR'
      }

      if (
        label.includes('sucesso') ||
        label.includes('success') ||
        label.includes('ok') ||
        label.includes('enviado') ||
        label.includes('integrado')
      ) {
        return 'SUCCESS'
      }

      return 'OTHER'
    },
    resolveStatusClass(statusGroup) {
      if (statusGroup === 'SUCCESS') return 'is-success'
      if (statusGroup === 'ERROR') return 'is-error'
      return 'is-other'
    },
    stringifyBlock(value) {
      if (value == null || value === '') return ''
      if (typeof value === 'string') return value

      try {
        return JSON.stringify(value, null, 2)
      } catch (error) {
        return String(value)
      }
    },
    parseStructuredValue(value) {
      if (value == null || value === '') return null
      if (typeof value === 'object') return value
      if (typeof value !== 'string') return value

      try {
        return JSON.parse(value)
      } catch (error) {
        return value
      }
    },
    extractReceitasList(payload, response) {
      const candidates = [payload, response]

      for (let i = 0; i < candidates.length; i += 1) {
        const current = candidates[i]
        if (!current || typeof current !== 'object') continue

        const receitas =
          current.receitas ||
          current.data && current.data.receitas ||
          current.response && current.response.receitas ||
          current.response && current.response.data && current.response.data.receitas

        if (Array.isArray(receitas)) {
          return receitas
        }
      }

      return []
    },
    openDetails(log) {
      this.selectedLog = log
      this.showDetailsModal = true
    },
    formatDateTime(value) {
      if (!value) return '-'
      return moment(value).format('DD/MM/YYYY HH:mm:ss')
    },
  },
}
</script>

<style scoped>
.integration-monitor {
  padding: 24px;
}

.monitor-hero,
.monitor-toolbar,
.monitor-table-card,
.monitor-placeholder {
  border: 1px solid #d9e5e7;
  border-radius: 20px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbfc 100%);
  box-shadow: 0 14px 30px rgba(14, 62, 70, 0.07);
}

.monitor-hero {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 24px;
}

.monitor-eyebrow {
  color: #0a7a64;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.monitor-title {
  margin-top: 10px;
  color: #0b3d46;
  font-size: 30px;
  font-weight: 800;
  line-height: 1.08;
}

.monitor-subtitle {
  max-width: 760px;
  margin-top: 10px;
  color: #58727a;
  font-size: 14px;
  line-height: 1.7;
}

.monitor-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  margin-top: 18px;
  padding: 18px 20px;
}

.monitor-toolbar-label {
  color: #69807a;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.monitor-toolbar-value {
  margin-top: 4px;
  color: #17453c;
  font-size: 18px;
  font-weight: 800;
}

.monitor-select-wrap {
  width: 320px;
  max-width: 100%;
}

.monitor-stats {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-top: 18px;
}

.monitor-stat-card {
  padding: 18px;
  border-radius: 16px;
  color: #ffffff;
}

.monitor-stat-card.neutral {
  background: linear-gradient(135deg, #0f5162 0%, #1f7385 100%);
}

.monitor-stat-card.success {
  background: linear-gradient(135deg, #0f7a62 0%, #18a381 100%);
}

.monitor-stat-card.danger {
  background: linear-gradient(135deg, #b2415d 0%, #d7657f 100%);
}

.monitor-stat-card.warning {
  background: linear-gradient(135deg, #b37417 0%, #d79a34 100%);
}

.monitor-stat-label {
  font-size: 12px;
  font-weight: 700;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.monitor-stat-value {
  margin-top: 10px;
  font-size: 34px;
  font-weight: 800;
  line-height: 1;
}

.monitor-table-card,
.monitor-placeholder {
  margin-top: 18px;
  padding: 22px;
}

.monitor-table-head {
  margin-bottom: 16px;
}

.monitor-table-title,
.monitor-placeholder-title {
  color: #0b3d46;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.2;
}

.monitor-table-subtitle,
.monitor-placeholder-copy {
  margin-top: 8px;
  color: #58727a;
  font-size: 14px;
  line-height: 1.65;
}

.monitor-placeholder-badge {
  display: inline-flex;
  padding: 6px 12px;
  border-radius: 999px;
  background: rgba(0, 142, 117, 0.1);
  color: #0b6b59;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.monitor-empty {
  color: #6b7f88;
  font-size: 14px;
}

.monitor-status-pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.monitor-status-pill.is-success {
  color: #166534;
  background: #dcfce7;
}

.monitor-status-pill.is-error {
  color: #b42318;
  background: #fee4e2;
}

.monitor-status-pill.is-other {
  color: #8a6116;
  background: #fff1cc;
}

.monitor-chip {
  display: inline-flex;
  padding: 4px 10px;
  border-radius: 999px;
  background: #f1f5f9;
  color: #334155;
  font-size: 11px;
  font-weight: 700;
}

.monitor-message-cell {
  min-width: 240px;
  white-space: normal;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.detail-item {
  padding: 12px 14px;
  border-radius: 14px;
  background: #f8fbfc;
  border: 1px solid #d9e5e7;
}

.detail-label {
  display: block;
  color: #67808a;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.detail-value {
  display: block;
  margin-top: 6px;
  color: #0f3440;
  font-size: 14px;
  font-weight: 700;
}

.detail-block-title {
  margin-bottom: 8px;
  color: #123d47;
  font-size: 13px;
  font-weight: 800;
}

.detail-box,
.detail-code {
  margin: 0;
  padding: 14px;
  border-radius: 14px;
  background: #f8fbfc;
  border: 1px solid #d9e5e7;
  color: #31454d;
  font-size: 13px;
}

.detail-code {
  white-space: pre-wrap;
  word-break: break-word;
}

@media (max-width: 991px) {
  .integration-monitor {
    padding: 16px;
  }

  .monitor-hero,
  .monitor-toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .monitor-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 767px) {
  .monitor-stats,
  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
